/**
 * kvBlob — the client-side seam for /api/data/:key KV blobs (and, through
 * readJsonAt, any other public JSON GET). One implementation
 * of the read ceremony (fetch → 404 fallback → JSON → Zod-validate → warn +
 * fallback on corrupt) and write (POST JSON, throw on non-2xx), so corrupt-blob
 * handling, 404 semantics, and error types can't drift between stores.
 *
 * Reads are public; writes attach the session cookie and the server enforces auth.
 */
import { z } from 'zod';

const DATA_BASE = '/api/data';

/**
 * The canonical /api/data error: carries the HTTP status so callers can
 * distinguish auth failures (401) from server errors. Historically named for
 * portfolioApi, which re-exports it — keep catching it under either name.
 */
export class PortfolioApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'PortfolioApiError';
  }
}

/**
 * Read a KV blob: 404 / null resolve to `fallback`; a present-but-schema-invalid
 * blob resolves to `fallback` with a console.warn (graceful read), and any other
 * non-2xx throws PortfolioApiError.
 *
 * `opts.throwOnInvalid` flips the schema-invalid case to THROW instead of
 * returning `fallback`. Read-modify-write callers MUST set this: if a corrupt
 * stored blob silently reads as empty, the subsequent whole-blob rewrite would
 * overwrite the still-stored (other portfolios') data with that empty base —
 * permanent data loss from a single bad row. (404/null still → fallback, since
 * an absent blob is legitimately empty and safe to write over.)
 */
export function readKvBlob<T>(
  key: string,
  // T must infer from the OUTPUT (what parse returns): schemas with .default()s
  // have a wider input than output.
  schema: z.ZodType<T, unknown>,
  fallback: T,
  opts?: { throwOnInvalid?: boolean },
): Promise<T> {
  return readJsonAt(`${DATA_BASE}/${key}`, schema, fallback, opts);
}

/**
 * The same read ceremony for any public JSON GET — the data Worker's
 * /api/news/* routes (news step N5) use it, where a 404 means "nothing
 * written yet" exactly as an absent blob does here.
 */
export async function readJsonAt<T>(
  path: string,
  schema: z.ZodType<T, unknown>,
  fallback: T,
  opts?: { throwOnInvalid?: boolean },
): Promise<T> {
  const resp = await fetch(path, { credentials: 'include' });
  if (resp.status === 404) return fallback;
  if (!resp.ok) {
    throw new PortfolioApiError(`${path} returned ${resp.status}`, resp.status);
  }
  const raw = (await resp.json()) as unknown;
  if (raw === null || raw === undefined) return fallback;
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    if (opts?.throwOnInvalid) {
      throw new PortfolioApiError(
        `${path} is corrupt (schema validation failed); refusing to overwrite it`,
        422,
      );
    }
    console.warn(`Schema validation failed for ${path}`, z.treeifyError(parsed.error));
    return fallback;
  }
  return parsed.data;
}

/** Write a KV blob (full replace — KV is last-write-wins). Throws PortfolioApiError on non-2xx. */
export async function writeKvBlob(key: string, value: unknown): Promise<void> {
  const resp = await fetch(`${DATA_BASE}/${key}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(value),
  });
  if (!resp.ok) {
    throw new PortfolioApiError(`/api/data/${key} POST returned ${resp.status}`, resp.status);
  }
}
